import { defineContract, nullableString, timestamp, z } from "./_base";

/*
 * Auth contracts.
 *
 * The API supports three ways in, all of them already built:
 *   - passkey  (WebAuthn / Touch ID / Face ID)  — also unlocks the data vault
 *   - email + password
 *
 * Google SSO (/auth/google) exists in the API but is deliberately NOT used
 * here. Signing in and granting Google data access are separate things, and
 * this app keeps them separate: you sign in with a credential you own, then
 * link Google from Settings.
 *
 * WebAuthn option payloads are passed straight through to @simplewebauthn
 * without being reshaped — they are a spec-defined structure, the library
 * validates them, and re-declaring them here would only create a second place
 * to get the spec wrong. Everything the *app* reads is still validated.
 */

export const authUserSchema = z.object({
  id: z.string(),
  email: z.string().optional(),
  displayName: nullableString,
  role: nullableString,
  plan: nullableString,
  picture: nullableString,
  authMethod: nullableString,
});
export type AuthUser = z.infer<typeof authUserSchema>;

/* ── Password ─────────────────────────────────────────────────────────── */
export const loginContract = defineContract({
  name: "auth.login",
  method: "POST",
  path: () => "/auth/login",
  response: z.object({
    token: z.string(),
    refreshToken: z.string().optional(),
    user: authUserSchema,
  }),
  params: (input: { email: string; password: string }) => input,
});

/* ── Passkey sign-in ──────────────────────────────────────────────────── */
export const passkeyAuthOptionsContract = defineContract({
  name: "passkey.auth.options",
  method: "POST",
  /* Omitting the email uses the discoverable-credential flow: the browser
     offers whichever passkey it holds for this site, so the user never types
     an identifier. That is the whole point of the design's unlock screen. */
  response: z.record(z.unknown()),
  path: () => "/passkey/auth/options",
  params: (input: { email?: string }) => input,
});

export const passkeyAuthVerifyContract = defineContract({
  name: "passkey.auth.verify",
  method: "POST",
  path: () => "/passkey/auth/verify",
  response: z.object({
    verified: z.boolean(),
    token: z.string(),
    user: authUserSchema,
    /* The passkey unwraps the key that decrypts vault fields (CNIC, passport,
       bank details). If this is false the session is authenticated but the
       encrypted fields stay unreadable. */
    vaultUnlocked: z.boolean().optional().default(false),
  }),
  params: (input: { response: unknown }) => input,
});

/* ── Passkey enrolment ────────────────────────────────────────────────── */
export const passkeyRegisterOptionsContract = defineContract({
  name: "passkey.register.options",
  method: "POST",
  path: () => "/passkey/register/options",
  response: z.record(z.unknown()),
});

export const passkeyRegisterVerifyContract = defineContract({
  name: "passkey.register.verify",
  method: "POST",
  path: () => "/passkey/register/verify",
  response: z.object({
    verified: z.boolean(),
    credentialId: z.string().optional(),
    deviceType: nullableString,
    backedUp: z.boolean().optional(),
    vaultCreated: z.boolean().optional(),
  }),
  params: (input: { response: unknown; deviceName?: string }) => input,
});

export const credentialSchema = z.object({
  credentialId: z.string(),
  deviceType: nullableString,
  backedUp: z.boolean().nullish(),
  deviceName: nullableString,
  lastUsedAt: timestamp,
  createdAt: timestamp,
});
export type PasskeyCredential = z.infer<typeof credentialSchema>;

export const credentialsContract = defineContract({
  name: "passkey.credentials",
  method: "GET",
  path: () => "/passkey/credentials",
  response: z.object({
    credentials: z.array(credentialSchema).optional().default([]),
    count: z.number().optional().default(0),
  }),
});

export const logoutContract = defineContract({
  name: "auth.logout",
  method: "POST",
  path: () => "/auth/logout",
  response: z.unknown(),
});
